import './lib/zod-config';
import { AGENT_TEMPLATE_IDS } from '@af/shared';
import type { QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { z } from 'zod';
import { AppShell } from '@/components/shell/AppShell';
import { Empty } from '@/components/ui/misc';
import { AGENT_TABS } from '@/lib/constants';
import { ONBOARDING_STEPS } from '@/lib/onboarding';
import { sessionQuery, setupQuery } from '@/lib/queries';

export interface RouterContext {
  queryClient: QueryClient;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => <Outlet />,
  notFoundComponent: () => <Empty icon="?">404 — no such page</Empty>,
});

// ---------- public ----------
const setupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/setup',
  component: lazyRouteComponent(() => import('@/routes/setup'), 'SetupPage'),
});

const registerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/register',
  component: lazyRouteComponent(() => import('@/routes/register'), 'RegisterPage'),
});
const impersonateRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/impersonate',
  component: lazyRouteComponent(() => import('@/routes/impersonate'), 'ImpersonatePage'),
});
const resetRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  component: lazyRouteComponent(() => import('@/routes/register'), 'PasswordResetPage'),
});
const billingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/billing',
  beforeLoad: async ({ context }) => {
    if (!(await context.queryClient.ensureQueryData(sessionQuery)))
      throw redirect({ to: '/login', search: { redirect: '/billing' } });
  },
  component: lazyRouteComponent(() => import('@/routes/billing'), 'BillingPage'),
});
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: z.object({ redirect: z.string().optional() }),
  beforeLoad: async ({ context }) => {
    const setup = await context.queryClient.ensureQueryData(setupQuery);
    if (setup.needsSetup) throw redirect({ to: '/setup' });
  },
  component: lazyRouteComponent(() => import('@/routes/login'), 'LoginPage'),
});

// ---------- authenticated shell ----------
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.ensureQueryData(sessionQuery);
    if (!session) {
      const setup = await context.queryClient.ensureQueryData(setupQuery);
      if (setup.needsSetup) throw redirect({ to: '/setup' });
      throw redirect({ to: '/login', search: { redirect: location.href } });
    }
    return { session };
  },
  component: AppShell,
});

const indexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/',
  component: lazyRouteComponent(() => import('@/routes/home'), 'HomePage'),
});

// The company overview and the agent list live on the chat page now (one page: talk + see everyone).
const companyRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/company',
  beforeLoad: () => {
    throw redirect({ to: '/chat', replace: true });
  },
});

const companiesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/companies',
  validateSearch: z.object({ new: z.boolean().optional() }),
  component: lazyRouteComponent(() => import('@/routes/companies'), 'CompaniesPage'),
});
const agentsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/agents',
  beforeLoad: () => {
    throw redirect({ to: '/chat', replace: true });
  },
});
const agentNewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/agents/new',
  // `template` / `blank`: skip the picker; `describe`: run the AI designer on arrival (from the chats screen).
  validateSearch: z.object({
    template: z.enum(AGENT_TEMPLATE_IDS).optional(),
    blank: z.boolean().optional(),
    describe: z.string().max(2000).optional(),
  }),
  component: lazyRouteComponent(() => import('@/routes/agents/new'), 'NewAgentPage'),
});
const agentDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/agents/$id',
  validateSearch: z.object({ tab: z.enum(AGENT_TABS).optional() }),
  component: lazyRouteComponent(() => import('@/routes/agents/detail'), 'AgentDetailPage'),
});

const onboardingRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/onboarding',
  validateSearch: z.object({ step: z.enum(ONBOARDING_STEPS).optional() }),
  component: lazyRouteComponent(() => import('@/routes/onboarding'), 'OnboardingPage'),
});

const chatRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/chat',
  // `new`: show the new-chat screen even when conversations are open; `agent`: preselect that agent.
  validateSearch: z.object({ agent: z.string().optional(), new: z.boolean().optional() }),
  component: lazyRouteComponent(() => import('@/routes/chat'), 'ChatPage'),
});
const chatConversationRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/chat/$conversationId',
  validateSearch: z.object({ agent: z.string().optional() }),
  component: lazyRouteComponent(() => import('@/routes/chat'), 'ChatPage'),
});

/** A channel or agent DM in the chat (Slack-style); `thread` opens a thread next to it. */
const chatChannelRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/chat/c/$channelId',
  validateSearch: z.object({ thread: z.string().optional() }),
  component: lazyRouteComponent(() => import('@/routes/chat'), 'ChatPage'),
});

const masterChatRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/master',
  component: lazyRouteComponent(() => import('@/routes/chat'), 'ChatPage'),
});

const tasksRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/tasks',
  validateSearch: z.object({ new: z.boolean().optional() }),
  component: lazyRouteComponent(() => import('@/routes/tasks'), 'TasksPage'),
});

const runsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/runs',
  validateSearch: z.object({ status: z.string().optional(), agent: z.string().optional() }),
  component: lazyRouteComponent(() => import('@/routes/runs'), 'RunsPage'),
});
const runDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/runs/$id',
  component: lazyRouteComponent(() => import('@/routes/run-detail'), 'RunDetailPage'),
});

const runAnalysisRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/run-analysis',
  component: lazyRouteComponent(() => import('@/routes/run-analysis'), 'RunAnalysisPage'),
});
const runAnalysisDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/run-analysis/$id',
  component: lazyRouteComponent(() => import('@/routes/run-analysis'), 'RunAnalysisDetailPage'),
});

const flowsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/flows',
  component: lazyRouteComponent(() => import('@/routes/flows/index'), 'FlowsPage'),
});
const flowDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/flows/$id',
  component: lazyRouteComponent(() => import('@/routes/flows/detail'), 'FlowDetailPage'),
});
const recordingRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/flows/recordings/$id',
  component: lazyRouteComponent(() => import('@/routes/flows/recording'), 'RecordingPage'),
});

const skillsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/skills',
  component: lazyRouteComponent(() => import('@/routes/skills/index'), 'SkillsPage'),
});
const skillDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/skills/$id',
  /** `archive`: open the archive confirmation (the Skills list's "not loaded in 30 days" marker links here). */
  validateSearch: z.object({ archive: z.boolean().optional() }),
  component: lazyRouteComponent(() => import('@/routes/skills/detail'), 'SkillDetailPage'),
});

const approvalsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/approvals',
  component: lazyRouteComponent(() => import('@/routes/approvals'), 'ApprovalsPage'),
});

const usageRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/usage',
  validateSearch: z.object({
    from: z.string().optional(),
    to: z.string().optional(),
    work: z.enum(['runs', 'background']).optional(),
    billing: z.enum(['api', 'subscription']).optional(),
    metric: z.enum(['cost', 'tokens']).optional(),
  }),
  component: lazyRouteComponent(() => import('@/routes/usage'), 'UsagePage'),
});

const notificationsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/notifications',
  component: lazyRouteComponent(() => import('@/routes/notifications'), 'NotificationsPage'),
});

const helpRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/help',
  validateSearch: z.object({ q: z.string().max(300).optional(), category: z.string().max(80).optional() }),
  component: lazyRouteComponent(() => import('@/routes/help/index'), 'HelpHomePage'),
});
const helpChatRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/help/chat',
  component: lazyRouteComponent(() => import('@/routes/help/chat'), 'HelpChatPage'),
});
const helpCategoryRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/help/c/$slug',
  component: lazyRouteComponent(() => import('@/routes/help/category'), 'HelpCategoryPage'),
});
const helpArticleRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/help/a/$slug',
  component: lazyRouteComponent(() => import('@/routes/help/article'), 'HelpArticlePage'),
});

const settingsIndexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings',
  beforeLoad: () => {
    throw redirect({ to: '/settings/$section', params: { section: 'providers' } });
  },
});
const settingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/$section',
  component: lazyRouteComponent(() => import('@/routes/settings'), 'SettingsPage'),
});

const routeTree = rootRoute.addChildren([
  setupRoute,
  loginRoute,
  registerRoute,
  resetRoute,
  impersonateRoute,
  billingRoute,
  appRoute.addChildren([
    indexRoute,
    companyRoute,
    companiesRoute,
    agentsRoute,
    agentNewRoute,
    agentDetailRoute,
    onboardingRoute,
    chatRoute,
    chatConversationRoute,
    chatChannelRoute,
    masterChatRoute,
    tasksRoute,
    runsRoute,
    runDetailRoute,
    runAnalysisRoute,
    runAnalysisDetailRoute,
    flowsRoute,
    flowDetailRoute,
    skillsRoute,
    skillDetailRoute,
    recordingRoute,
    approvalsRoute,
    usageRoute,
    notificationsRoute,
    helpRoute,
    helpChatRoute,
    helpCategoryRoute,
    helpArticleRoute,
    settingsIndexRoute,
    settingsRoute,
  ]),
]);

export function createAppRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: 'intent',
    defaultPreloadStaleTime: 0,
    scrollRestoration: true,
  });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
